import React, { useState, useEffect, useRef, useMemo, useContext, cloneElement, Children } from 'react';
import { DateRangeContext } from '../contexts/DateRangeContext';
import { formatPulledStamp, formatRangeLabel } from '../utils/formatters';
import { EXPORT_FONT, svgToImage, downloadBlob } from '../utils/svgExport';
import { buildTableCsv, csvBlob, csvFileName } from '../utils/tableCsv';

function isResponsiveContainer(child) {
  if (!child || !child.props) return false;
  if (child.type?.displayName === 'ResponsiveContainer') return true;
  if (child.props.width === '100%' && typeof child.props.height === 'number') return true;
  return false;
}

// Every shared image is rendered at this one landscape size, whatever the
// device and whether or not the chart is expanded. The chart is laid out
// off-screen at these dimensions rather than scaling whatever happens to be
// on screen — stretching a phone-width chart up would give oversized text and
// whatever sparse tick density recharts chose for a narrow box.
const EXPORT_W = 1200;
const EXPORT_H = 675;
const EXPORT_PAD = 24;
const EXPORT_TITLE_H = 48;

const EXPORT_RANGE_H = 22;   // subtitle line naming the period
const EXPORT_FOOTER_H = 28;  // "Pulled on ..." stamp

function exportPlotSize(hasLegend, hasNote, hasRange) {
  return {
    w: EXPORT_W - EXPORT_PAD * 2,
    h: EXPORT_H - EXPORT_TITLE_H - (hasRange ? EXPORT_RANGE_H : 0)
       - (hasLegend ? 38 : 12) - (hasNote ? 26 : 0) - EXPORT_FOOTER_H - EXPORT_PAD,
  };
}

// Isolate the chart itself from anything else the card was given (a series
// toggle row, a filter control), so the off-screen stage holds only the chart
// and it fills the stage exactly.
function chartChildrenOnly(children) {
  const charts = Children.toArray(children).filter(isResponsiveContainer);
  const source = charts.length ? charts : Children.toArray(children);
  return Children.map(source, child =>
    isResponsiveContainer(child)
      ? cloneElement(child, { width: '100%', height: '100%', children: disableAnimation(child.props.children) })
      : child
  );
}

// Recharts reveals series with an enter animation, so a naive capture catches
// a half-drawn line. Turn it off on the export copy.
const ANIMATED_SERIES = new Set(['Line', 'Bar', 'Area', 'Pie', 'Scatter', 'Radar', 'RadialBar', 'Funnel']);

function disableAnimation(node) {
  return Children.map(node, child => {
    if (!React.isValidElement(child)) return child;
    const name = child.type?.displayName || child.type?.name;
    const next = {};
    if (ANIMATED_SERIES.has(name)) next.isAnimationActive = false;
    if (child.props?.children) next.children = disableAnimation(child.props.children);
    return Object.keys(next).length ? cloneElement(child, next) : child;
  });
}

function makeFullHeight(children) {
  return Children.map(children, child => {
    if (isResponsiveContainer(child)) {
      return cloneElement(child, { height: '100%' });
    }
    return child;
  });
}

// Read the legend straight off the DOM: recharts renders it as HTML, not as
// part of the chart SVG, so it has to be drawn onto the canvas by hand.
// Charts that build their own key instead of using a recharts <Legend> mark
// it with `.chart-key`; without this an exported multi-series chart is just
// unlabelled coloured lines.
function readLegend(containerEl) {
  const custom = [...containerEl.querySelectorAll('.chart-key button')]
    .filter(btn => parseFloat(window.getComputedStyle(btn).opacity || '1') > 0.9)
    .map(btn => {
      const swatch = btn.querySelector('span');
      return {
        text: btn.textContent.trim(),
        color: swatch ? window.getComputedStyle(swatch).backgroundColor : '#666',
      };
    })
    .filter(l => l.text);
  if (custom.length) return custom;

  return [...containerEl.querySelectorAll('.recharts-legend-item')].map(item => {
    const text = item.querySelector('.recharts-legend-item-text')?.textContent || '';
    const swatch = item.querySelector('.recharts-surface, svg');
    let color = '#666';
    const shape = swatch?.querySelector('path, rect, line, circle');
    if (shape) {
      const fill = shape.getAttribute('fill');
      const stroke = shape.getAttribute('stroke');
      color = (fill && fill !== 'none' ? fill : null)
        || (stroke && stroke !== 'none' ? stroke : null)
        || window.getComputedStyle(shape).fill
        || color;
    }
    return { text, color };
  }).filter(l => l.text);
}

async function chartToPngBlob(containerEl, exportSvg, plot, rangeLabel) {
  const mainSvg = exportSvg;
  if (!mainSvg) throw new Error('no chart to export');

  const svgW = plot.w;
  const svgH = plot.h;

  const scale = 2;
  const pad = EXPORT_PAD;
  const titleH = EXPORT_TITLE_H;
  const legend = readLegend(containerEl);
  const legendH = legend.length ? 38 : 12;
  const noteText = containerEl.querySelector('.chart-note')?.textContent?.trim() || '';
  const noteH = noteText ? 26 : 0;

  // Fixed output, so the same chart shared from a phone and from a desktop
  // produces byte-identical dimensions.
  const cssW = EXPORT_W;
  const cssH = EXPORT_H;

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(cssW * scale);
  canvas.height = Math.round(cssH * scale);
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);
  ctx.textBaseline = 'alphabetic';

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, cssW, cssH);

  const title = containerEl.querySelector('.chart-title')?.textContent?.trim();
  if (title) {
    ctx.fillStyle = '#1a202c';
    ctx.font = `600 16px ${EXPORT_FONT}`;
    ctx.fillText(title, pad, 27);
  }

  // The period the figures cover, directly under the title.
  const headerH = titleH + (rangeLabel ? EXPORT_RANGE_H : 0);
  if (rangeLabel) {
    ctx.fillStyle = '#4a5568';
    ctx.font = `13px ${EXPORT_FONT}`;
    ctx.fillText(rangeLabel, pad, titleH + 12);
  }

  const img = await svgToImage(mainSvg, svgW, svgH);
  if (!img) throw new Error('could not rasterize the chart');
  ctx.drawImage(img, pad, headerH, svgW, svgH);

  if (legend.length) {
    // Lay the legend out from measured text rather than from DOM widths, so
    // it stays centred and evenly spaced regardless of how the live legend
    // happened to wrap on screen.
    ctx.font = `12px ${EXPORT_FONT}`;
    const sw = 12, gap = 6, between = 20;
    const widths = legend.map(l => sw + gap + ctx.measureText(l.text).width);
    const total = widths.reduce((a, b) => a + b, 0) + between * (legend.length - 1);
    let x = Math.max(pad, (cssW - total) / 2);
    const y = headerH + svgH + 24;
    legend.forEach((l, i) => {
      ctx.fillStyle = l.color;
      ctx.fillRect(x, y - 9, sw, sw);
      ctx.fillStyle = '#334155';
      ctx.fillText(l.text, x + sw + gap, y);
      x += widths[i] + between;
    });
  }

  if (noteText) {
    ctx.font = `italic 11px ${EXPORT_FONT}`;
    ctx.fillStyle = '#718096';
    ctx.fillText(noteText, pad, headerH + svgH + legendH + 16);
  }

  // Grey stamp, bottom right.
  ctx.textAlign = 'right';
  ctx.font = `12px ${EXPORT_FONT}`;
  ctx.fillStyle = '#94a3b8';
  ctx.fillText(formatPulledStamp(), cssW - pad, cssH - 12);
  ctx.textAlign = 'left';

  const blob = await new Promise(r => canvas.toBlob(r, 'image/png'));
  if (!blob) throw new Error('could not encode the image');
  return blob;
}

// A table inside a card registers its full, sorted dataset here, so the share
// button can export every row rather than the page currently on screen — these
// tables paginate client-side, so the DOM holds 10 of 413.
export const ChartTableExport = React.createContext(null);

function exportFileName(containerEl) {
  const title = containerEl.querySelector('.chart-title')?.textContent || 'chart';
  return title.replace(/[^a-zA-Z0-9 ]/g, '').trim().replace(/\s+/g, '-') + '.png';
}

// `controls` are the card's own widgets (row count, page size, a filter, a
// grouping toggle). They render on the header line beside the title so a table
// card doesn't spend a whole empty row on them.
export default function ChartCard({ title, titleText, note, controls, style, children }) {
  // `title` may be a node (a heading plus an info icon, say). Screen-reader
  // labels below interpolate it into strings, which would render a node as
  // "[object Object]" — so callers passing a node also pass plain `titleText`.
  const titleLabel = titleText ?? (typeof title === 'string' ? title : '');
  const [expanded, setExpanded] = useState(false);
  const [shared, setShared] = useState(false);
  const [fullShared, setFullShared] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [fullDownloaded, setFullDownloaded] = useState(false);
  const [shareError, setShareError] = useState('');
  // Off-screen stage the chart is re-laid-out in at the canonical export size.
  const [exportPlot, setExportPlot] = useState(null);
  const cardRef = useRef(null);
  const fullRef = useRef(null);
  const stageRef = useRef(null);
  // Tables that have announced themselves inside this card. Keyed by node so
  // the card copy and the expanded copy each keep their own sort state, and
  // sharing uses whichever one you pressed.
  const publishedRange = useContext(DateRangeContext);
  const tablesRef = useRef([]);
  const [hasTable, setHasTable] = useState(false);
  const tableExport = useMemo(() => ({
    register(node, data) {
      const list = tablesRef.current;
      const i = list.findIndex(e => e.node === node);
      if (i >= 0) list[i] = { node, ...data }; else list.push({ node, ...data });
      setHasTable(true);
    },
    unregister(node) {
      tablesRef.current = tablesRef.current.filter(e => e.node !== node);
      setHasTable(tablesRef.current.length > 0);
    },
  }), []);

  useEffect(() => {
    if (!expanded) return;
    const handleKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
    document.addEventListener('keydown', handleKey);
    // Freeze the page underneath, or a drag along the chart's axis scrolls it
    // instead of moving the tooltip. The class alone is not enough: pinning
    // the body takes it out of flow, so the scroll offset has to be carried
    // as an inline `top` and put back on close. See the .chart-expanded rules
    // for why locking the body's overflow on its own does nothing here.
    const scrollY = window.scrollY;
    document.documentElement.classList.add('chart-expanded');
    document.body.classList.add('chart-expanded');
    document.body.style.top = `-${scrollY}px`;
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.documentElement.classList.remove('chart-expanded');
      document.body.classList.remove('chart-expanded');
      document.body.style.top = '';
      window.scrollTo(0, scrollY);
    };
  }, [expanded]);

  // Mount the off-screen stage and wait for recharts to lay the chart out in
  // it and finish its enter animation, so the capture is a settled frame.
  const renderExportChart = async (plot) => {
    setExportPlot(plot);
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
    const deadline = Date.now() + 3000;
    let svg = null;
    while (Date.now() < deadline) {
      svg = stageRef.current?.querySelector('svg.recharts-surface');
      if (svg && svg.querySelector('.recharts-cartesian-axis-tick, .recharts-pie-sector, .recharts-layer')) break;
      await new Promise(r => setTimeout(r, 50));
    }
    if (!svg) throw new Error('the chart did not render for export');

    // Animation is disabled above, but rather than trust that for every chart
    // type, wait until the rendered geometry stops changing. Settles in about
    // two frames when animation really is off.
    const geometry = () => [...stageRef.current.querySelectorAll('svg.recharts-surface path')]
      .map(el => el.getAttribute('d') || '').join('|').length;
    let last = -1, stable = 0;
    while (Date.now() < deadline && stable < 2) {
      await new Promise(r => setTimeout(r, 80));
      const now = geometry();
      stable = now === last ? stable + 1 : 0;
      last = now;
    }
    return stageRef.current.querySelector('svg.recharts-surface');
  };

  // Build the file once; share and download differ only in what they do with
  // it. A table card yields its data as CSV, a chart card a PNG.
  const buildExport = async (el) => {
    const rangeLabel = formatRangeLabel(publishedRange?.current);
    const table = tablesRef.current.find(t => el.contains(t.node));
    try {
      if (table) {
        const cardTitle = el.querySelector('.chart-title')?.textContent?.trim();
        const csv = buildTableCsv(table.columns, table.rows, { title: cardTitle, rangeLabel });
        return {
          blob: csvBlob(csv),
          fileName: csvFileName(cardTitle || 'chart'),
          mime: 'text/csv',
        };
      }
      const plot = exportPlotSize(
        readLegend(el).length > 0,
        !!el.querySelector('.chart-note')?.textContent?.trim(),
        !!rangeLabel
      );
      const exportSvg = await renderExportChart(plot);
      return {
        blob: await chartToPngBlob(el, exportSvg, plot, rangeLabel),
        fileName: exportFileName(el),
        mime: 'image/png',
      };
    } finally {
      setExportPlot(null);
    }
  };

  const flash = (setFlag) => {
    setFlag(true);
    setTimeout(() => setFlag(false), 1500);
  };

  const reportFailure = (err) => {
    // Previously every failure was swallowed and the button just did nothing,
    // which is indistinguishable from a dead button.
    console.error('Chart export failed:', err);
    setShareError("Couldn't build the file");
    setTimeout(() => setShareError(''), 2500);
  };

  const handleShare = async (ref, setFlag) => {
    const el = ref.current;
    if (!el) return;
    setShareError('');
    let out;
    try {
      out = await buildExport(el);
    } catch (err) {
      reportFailure(err);
      return;
    }

    const file = new File([out.blob], out.fileName, { type: out.mime });
    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file] });
        flash(setFlag);
        return;
      } catch (err) {
        // The user dismissing the share sheet is not an error — leave it be.
        if (err?.name === 'AbortError') return;
        // Anything else (Safari withholding the share sheet because the user
        // gesture expired while the file was being built, an unsupported
        // payload) should still get them the file.
        console.warn('Share sheet unavailable, downloading instead:', err);
      }
    }
    downloadBlob(out.blob, out.fileName);
    flash(setFlag);
  };

  // Always saves to the device — never opens the share sheet.
  const handleDownload = async (ref, setFlag) => {
    const el = ref.current;
    if (!el) return;
    setShareError('');
    try {
      const out = await buildExport(el);
      downloadBlob(out.blob, out.fileName);
      flash(setFlag);
    } catch (err) {
      reportFailure(err);
    }
  };

  return (
    <ChartTableExport.Provider value={tableExport}>
      <div ref={cardRef} className={`chart-card ${expanded ? 'chart-card-hidden' : ''}`} style={style}>
        <div className="chart-header">
          <div className="chart-title">{title}</div>
          {controls && <div className="chart-controls">{controls}</div>}
          <div className="chart-actions">
            <button className="chart-expand-btn" onClick={() => handleShare(cardRef, setShared)} title={shared ? 'Shared!' : hasTable ? 'Share as CSV' : 'Share chart'}
              aria-label={shared ? 'Shared' : hasTable ? `Export ${titleLabel} as CSV` : `Share ${titleLabel} chart`}>
              {shared ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
              )}
            </button>
            <button className="chart-expand-btn" onClick={() => handleDownload(cardRef, setDownloaded)}
              title={downloaded ? 'Saved!' : hasTable ? 'Download CSV' : 'Download image'}
              aria-label={downloaded ? 'Saved' : hasTable ? `Download ${titleLabel} as CSV` : `Download ${titleLabel} as an image`}>
              {downloaded ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" /><line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              )}
            </button>
            <button className="chart-expand-btn" onClick={() => setExpanded(true)} title="Expand" aria-label={`Expand ${titleLabel} chart`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            </button>
          </div>
        </div>
        {children}
        {note && <div className="chart-note">{note}</div>}
        {shareError && <div className="chart-share-error" role="status">{shareError}</div>}
      </div>

      {/* Rendered off-screen only while exporting, at the fixed landscape
          size, so the shared PNG never depends on the viewport or on whether
          the chart happens to be expanded. */}
      {exportPlot && (
        <div className="chart-export-stage" aria-hidden="true">
          <div ref={stageRef} style={{ width: exportPlot.w, height: exportPlot.h }}>
            {chartChildrenOnly(children)}
          </div>
        </div>
      )}

      {expanded && (
        <div className="chart-fullscreen-overlay" role="dialog" aria-modal="true" aria-label={`${titleLabel} expanded`} onClick={() => setExpanded(false)}>
          <div ref={fullRef} className={`chart-fullscreen-container ${hasTable ? 'has-table' : ''}`} onClick={e => e.stopPropagation()}>
            <div className="chart-header">
              <div className="chart-title" style={{ fontSize: '18px' }}>{title}</div>
              {controls && <div className="chart-controls">{controls}</div>}
              <div className="chart-actions">
                <button className="chart-expand-btn" onClick={() => handleShare(fullRef, setFullShared)} title={fullShared ? 'Shared!' : hasTable ? 'Share as CSV' : 'Share chart'}
                  aria-label={fullShared ? 'Shared' : hasTable ? `Export ${titleLabel} as CSV` : `Share ${titleLabel} chart`}>
                  {fullShared ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                    </svg>
                  )}
                </button>
                <button className="chart-expand-btn" onClick={() => handleDownload(fullRef, setFullDownloaded)}
                  title={fullDownloaded ? 'Saved!' : hasTable ? 'Download CSV' : 'Download image'}
                  aria-label={fullDownloaded ? 'Saved' : hasTable ? `Download ${titleLabel} as CSV` : `Download ${titleLabel} as an image`}>
                  {fullDownloaded ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" /><line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  )}
                </button>
                <button className="chart-expand-btn" onClick={() => setExpanded(false)} title="Close" aria-label="Close expanded chart">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="4 14 10 14 10 20" /><polyline points="20 10 14 10 14 4" />
                    <line x1="14" y1="10" x2="21" y2="3" /><line x1="3" y1="21" x2="10" y2="14" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="chart-rotate-hint">Turn your phone sideways for a wider view</div>
            <div className="chart-fullscreen-content">
              {makeFullHeight(children)}
            </div>
            {note && <div className="chart-note">{note}</div>}
          </div>
        </div>
      )}
    </ChartTableExport.Provider>
  );
}
